import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { openModal } from "../../../../../redux/actions/modalActions";
import "./SingleProgram.css";
import "../../../../../components/commons/Modal.css";
import { imageLoader } from "../../../../../utils/imageLoader";

const Documents = ({ programDocuments }) => {
  const dispatch = useDispatch();
  const groupedDocuments = useMemo(() => {
    if (!programDocuments) return {};

    const pdfDocuments = programDocuments.filter(doc =>
      doc?.document?.[0]?.url?.toLowerCase().includes('.pdf')
    );

    return pdfDocuments.reduce((acc, doc) => {
      const type = doc.type.split("_")[0].toUpperCase();
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(doc);
      return acc;
    }, {});
  }, [programDocuments]);

  const formatDocumentType = (type) => {
    if (type.toUpperCase().includes("OTHER")) {
      return "Autre Documents";
    }
    return type
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const DocumentGallery = ({ docs, initialIndex = 0 }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const handlePrev = useCallback(() => {
      if (docs.length > 1) {
        setCurrentIndex((prev) => (prev === 0 ? docs.length - 1 : prev - 1));
      }
    }, [docs.length]);

    const handleNext = useCallback(() => {
      if (docs.length > 1) {
        setCurrentIndex((prev) => (prev === docs.length - 1 ? 0 : prev + 1));
      }
    }, [docs.length]);

    useEffect(() => {
      const onKey = (e) => {
        if (e.key === "ArrowLeft") handlePrev();
        if (e.key === "ArrowRight") handleNext();
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }, [docs.length, handlePrev, handleNext]);

    if (!docs || docs.length === 0) return null;

    const currentDoc = docs[currentIndex];

    return (
      <div className="image-gallery-container" style={{ height: "80vh" }}>
        <iframe
          src={imageLoader(currentDoc.document[0].url)}
          title={formatDocumentType(currentDoc.type)}
          className="gallery-image"
          style={{ width: "100%", height: "100%", border: "none", borderRadius: 5 }}
        />
        {docs.length > 1 && (
          <>
            <button className="nav-button nav-prev" onClick={handlePrev} aria-label="Document précédent">‹</button>
            <button className="nav-button nav-next" onClick={handleNext} aria-label="Document suivant">›</button>
            <div className="image-dots">
              {docs.map((_, idx) => (
                <button
                  key={idx}
                  className={`dot ${idx === currentIndex ? "active" : ""}`}
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Aller au document ${idx + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const handleDocumentClick = (docs, initialIndex) => {
    const modalContent = (
      <DocumentGallery docs={docs} initialIndex={initialIndex} />
    );

    dispatch(openModal({
      title: formatDocumentType(docs[initialIndex]?.type || "Document"),
      content: modalContent
    }));
  };

  if (!programDocuments || programDocuments.length === 0) {
    return (
      <section id="documents" className="mt-70">
        <p className="semibold fs16 pink mb-10">Documents</p>
        <div className="bg-white radius5 px-20 py-20">
          <div className="text-muted">Pas de documents disponibles</div>
        </div>
      </section>
    );
  }

  return (
    <section id="documents" className="mt-70">
      <p className="semibold fs16 pink mb-10">Documents</p>
      <div className="bg-white radius5 px-20 py-20">
        <div className="accordion" id="accordionDocuments">
          {Object.entries(groupedDocuments).map(([type, docs], index) => (
            <div className="accordion-item" key={type}>
              <h2 className="accordion-header" id={`heading-${type}`}>
                <button
                  className="accordion-button semibold fs21 darkGrey bg-soft-pink"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse-${type}`}
                  aria-expanded={index === 0}
                  aria-controls={`collapse-${type}`}
                >
                  {type === "OTHER" ? "Autre Documents" : type}
                </button>
              </h2>
              <div
                id={`collapse-${type}`}
                className={`accordion-collapse collapse ${index === 0 ? "show" : ""
                  }`}
                aria-labelledby={`heading-${type}`}
              >
                <div className="accordion-body gap20">
                  {docs.map((pdf, idx) => (
                    <div
                      key={pdf.documentId}
                      onClick={() => handleDocumentClick(docs, idx)}
                      className="d-flex align-items-center"
                      style={{ cursor: 'pointer' }}
                      title="Voir le document"
                    >
                      <iframe title={formatDocumentType(pdf.name)} style={{ height: "250px", pointerEvents: "none" }} src={imageLoader(pdf.document[0].url)} frameBorder="0">
                      </iframe>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Documents;
